import { bootcamps } from "@/content/bootcamps";
import { FR_INTRO_VIDEO_URLS } from "@/content/bootcamp-video-urls-fr";

/**
 * DB bootcamp slugs don't match the content-file slugs that hold the
 * HeyGen intro video URLs. This map bridges DB slug -> local content slug.
 * Add new entries here as new published DB bootcamps need a video.
 */
const DB_TO_LOCAL_SLUG: Record<string, string> = {
  "zero-investment-launch": "starting-business-zero-investment",
  "startup-confidence": "startup-confidence-launch-assurance",
  "growth-scaling-ops": "growing-scaling-your-business",
  "emerging-leaders": "leadership-excellence-emerging-leaders",
  // "Launch your MVP in 30 days" has no exact local twin; use the closest
  // growth-hacking presenter as a stand-in so the card has a video.
  "launch-mvp-in-30-days": "digital-product-growth-hacking",
  // getting-started-bootpreneurship and launch-101-free are free primers
  // with dedicated HeyGen intros — resolved via the EN/FR DB-slug maps
  // below rather than a local content twin.
};

const FR_URLS = FR_INTRO_VIDEO_URLS;

/**
 * Dedicated English intros for DB-only bootcamp slugs that have no
 * corresponding entry in `src/content/bootcamps.ts`. Keyed by DB slug.
 */
const EN_INTRO_VIDEO_URLS: Record<string, string> = {
  "getting-started-bootpreneurship":
    "/__l5e/assets-v1/6fe7ba99-dbe3-463e-9b5f-f4fb9e54b5d7/b520ee1a1c474d2bbc5017e6ba92bf79.mp4",
  "launch-101-free":
    "/__l5e/assets-v1/2e5a96fe-503c-460a-bf31-37bc52372d24/1ea2fb8cd3324c859ed189151f945b86.mp4",
};

/**
 * Dedicated French intros for DB-only bootcamp slugs (same keying as
 * EN_INTRO_VIDEO_URLS). Consulted before falling back to the shared
 * FR_URLS map (which is keyed by local content slugs).
 */
const EXTRA_FR_INTRO_VIDEO_URLS: Record<string, string> = {
  "getting-started-bootpreneurship":
    "/__l5e/assets-v1/fd304e90-64d9-431a-be6f-2bb395b07ba0/23aecc776f6a48bc8ed453c4b9c7aeef.mp4",
  "launch-101-free":
    "/__l5e/assets-v1/90bfcc64-3eef-4b52-9641-cf401fe2548f/a9fe2cc82c4e43f9ba1ee7ca405d547e.mp4",
};

/**
 * Return the intro video URL for any bootcamp slug, whether it comes from
 * the DB (mapped via DB_TO_LOCAL_SLUG) or directly matches a local entry.
 *
 * When `lang` starts with "fr", a native French render is preferred. If no
 * FR render exists for that slug, we fall back to the English video so the
 * card still has a video instead of only the SVG cover.
 */
export function getIntroVideoForSlug(slug: string | null | undefined, lang?: string): string | undefined {
  if (!slug) return undefined;
  const localSlug = DB_TO_LOCAL_SLUG[slug] ?? slug;
  if (lang && lang.toLowerCase().startsWith("fr")) {
    const extraFr = EXTRA_FR_INTRO_VIDEO_URLS[slug];
    if (extraFr) return extraFr;
    const fr = FR_URLS[localSlug];
    if (fr) return fr;
  }
  const en = EN_INTRO_VIDEO_URLS[slug];
  if (en) return en;
  const bc = bootcamps.find((b) => b.slug === localSlug);
  return bc?.intro_video_url;
}